/**
 * The BUDGET card and "Change budget" (TMMAPP-0111; MM-D-118; flagged: v2 doesn't draw budget history —
 * built from its patterns, the card like "Categories in this envelope"). A BUDGET envelope's dated
 * budgets from `GET /v1/envelopes/{id}/budgets`, newest first: "{amount} from {date}" at 13px, the one
 * in effect today marked "Current" (`micro`), and "who · when" at 12px beneath; a writer gets an
 * outlined "CHANGE BUDGET". The dialog (460) takes an Amount and "Takes effect" (a civil date, past or
 * future) and saves through the envelope `PATCH` with `targetEffectiveFrom`. A past date re-reads the
 * past only from that date; a future one leaves today's budget in effect until then — the server
 * compares each period with the budget in effect on its last day, so the narrative and progress the
 * page reads are always the served ones. An empty Amount is "no budget" from that date
 * (`targetMinor: null`, TMMAPI-0169; TMMAPP-0136 item 11), listed as "No budget from {date}".
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { envelopesApi, type EnvelopeBudget } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { ErrorState } from '@/components/ErrorState';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import {
  formatCivilDate,
  formatInstant,
  formatMoneyMinor,
  minorToInputText,
  parseMoneyToMinor,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { budgetInEffect } from './budgetInEffect';

const OVERLINE = {
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
} as const;

interface BudgetHistoryCardProps {
  envelopeId: number;
  envelopeName: string;
  currency: string | null;
  canWrite: boolean;
  /** After a change: the page re-reads the envelope (its served target, progress and narrative). */
  onChanged: () => Promise<void> | void;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
}

export function BudgetHistoryCard({
  envelopeId,
  envelopeName,
  currency,
  canWrite,
  onChanged,
  onNotify,
  onSubscriptionRequired,
}: BudgetHistoryCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const timeZone = me.user.timezone ?? 'UTC';
  const today = todayCivilInZone(timeZone);
  const [budgets, setBudgets] = useState<EnvelopeBudget[] | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [changing, setChanging] = useState(false);
  const changeButton = useRef<HTMLButtonElement | null>(null);

  const load = useCallback(async () => {
    try {
      setBudgets(await envelopesApi.budgets(envelopeId));
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, [envelopeId]);

  // StrictMode double-invokes the effect; the history is read once per envelope.
  const loaded = useRef<number | null>(null);
  useEffect(() => {
    if (loaded.current === envelopeId) return;
    loaded.current = envelopeId;
    void load();
  }, [envelopeId, load]);

  const current = budgets === null ? null : budgetInEffect(budgets, today);
  const label = tokens.textLabel('surface');

  return (
    <Paper
      component="section"
      aria-labelledby="envelope-budget-title"
      sx={{ p: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}
      >
        <Typography id="envelope-budget-title" component="h2" sx={{ ...OVERLINE, color: label }}>
          {t('budgetHistory.title')}
        </Typography>
        {canWrite && (
          <Button
            ref={changeButton}
            variant="outlined"
            size="small"
            onClick={() => setChanging(true)}
          >
            {t('budgetHistory.change')}
          </Button>
        )}
      </Stack>
      {failure !== null && budgets === null && (
        <ErrorState
          variant="section"
          error={failure.error}
          what={t('budgetHistory.what')}
          onRetry={() => void load()}
        />
      )}
      {budgets === null && failure === null && (
        <Skeleton variant="rounded" height={48} aria-busy="true" />
      )}
      {budgets !== null && budgets.length === 0 && (
        <Typography sx={{ fontSize: 13, color: label }}>{t('budgetHistory.none')}</Typography>
      )}
      {budgets !== null && budgets.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {budgets.map((budget) => {
            const isCurrent = budget === current;
            const who = [
              budget.createdByName,
              formatInstant(budget.createdAt, locale, timeZone),
            ].filter((part): part is string => part !== null && part.length > 0);
            return (
              <Box
                component="li"
                key={`${budget.effectiveFrom}-${budget.createdAt}`}
                data-budget-entry={budget.effectiveFrom}
                sx={{
                  py: '6px',
                  borderTop: `1px solid ${tokens.line}`,
                  '&:first-of-type': { borderTop: 0 },
                }}
              >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                    {budget.targetMinor === null
                      ? t('budgetHistory.entryNone', {
                          date: formatCivilDate(budget.effectiveFrom, locale),
                        })
                      : t('budgetHistory.entry', {
                          amount: formatMoneyMinor(budget.targetMinor, currency, locale, false, {
                            whole: true,
                          }),
                          date: formatCivilDate(budget.effectiveFrom, locale),
                        })}
                  </Typography>
                  {isCurrent && (
                    <Typography variant="micro" component="span" sx={{ color: tokens.link }}>
                      {t('budgetHistory.current')}
                    </Typography>
                  )}
                </Stack>
                {who.length > 0 && (
                  <Typography sx={{ fontSize: 12, color: label }}>{who.join(' · ')}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      {changing && (
        <ChangeBudgetDialog
          envelopeId={envelopeId}
          envelopeName={envelopeName}
          currency={currency}
          initialMinor={current?.targetMinor ?? null}
          today={today}
          onClose={() => {
            setChanging(false);
            changeButton.current?.focus();
          }}
          onSaved={async () => {
            setChanging(false);
            onNotify(t('budgetHistory.saved'));
            await Promise.all([load(), onChanged()]);
            changeButton.current?.focus();
          }}
          onSubscriptionRequired={() => {
            setChanging(false);
            onSubscriptionRequired();
          }}
        />
      )}
    </Paper>
  );
}

interface ChangeBudgetDialogProps {
  envelopeId: number;
  envelopeName: string;
  currency: string | null;
  initialMinor: number | null;
  today: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onSubscriptionRequired: () => void;
}

export function ChangeBudgetDialog({
  envelopeId,
  envelopeName,
  currency,
  initialMinor,
  today,
  onClose,
  onSaved,
  onSubscriptionRequired,
}: ChangeBudgetDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const titleId = useId();
  const subtitleId = useId();
  const fullScreen = useDialogFullScreen();
  const initialAmount =
    initialMinor === null ? '' : minorToInputText(initialMinor, currency, locale);
  const [amount, setAmount] = useState(initialAmount);
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ amount?: string; date?: string }>({});
  const dirty = amount !== initialAmount || effectiveFrom !== today;
  const guard = useDialogDirtyGuard(dirty, onClose, busy);
  const cleared = amount.trim().length === 0;
  const minor = cleared ? null : parseMoneyToMinor(amount, currency, locale);
  const valid =
    (cleared || (minor !== null && minor > 0)) && /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await envelopesApi.update(envelopeId, {
        targetMinor: cleared ? null : minor,
        targetEffectiveFrom: effectiveFrom,
      });
      await onSaved();
    } catch (caught) {
      if (isApiError(caught) && caught.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (isApiError(caught) && caught.code === ErrorCodes.VALIDATION_FAILED) {
        const next: { amount?: string; date?: string } = {};
        const result = applyBackendFields(
          caught,
          ['targetMinor', 'targetEffectiveFrom'],
          (field, message) => {
            if (field === 'targetMinor') next.amount = message;
            if (field === 'targetEffectiveFrom') next.date = message;
          },
          t,
          guard.focusErrors,
        );
        setFieldErrors(next);
        setError(validationAlert(result, caught, t, t('virtualAccount.errorGeneric')));
      } else {
        setError(t('virtualAccount.errorGeneric'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitleId={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="xs"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 460 } }}
    >
      <Box
        component="form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => void handleSubmit(event)}
        noValidate
      >
        <DialogTitle id={titleId}>{t('budgetHistory.dialogTitle')}</DialogTitle>
        <DialogSubtitle id={subtitleId}>
          {t('budgetHistory.dialogSub', { name: envelopeName })}
        </DialogSubtitle>
        <DialogContent>
          <Stack spacing={1.75} sx={{ mt: 1 }}>
            {error !== null && (
              <Typography role="alert" sx={{ fontSize: 13, color: tokens.errorText }}>
                {error}
              </Typography>
            )}
            <TextField
              label={t('budgetHistory.amount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              fullWidth
              error={fieldErrors.amount !== undefined}
              helperText={fieldErrors.amount ?? t('budgetHistory.amountHelp')}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              type="date"
              label={t('budgetHistory.takesEffect')}
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
              required
              fullWidth
              error={fieldErrors.date !== undefined}
              helperText={fieldErrors.date ?? t('budgetHistory.takesEffectHelp')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={!valid} loading={busy}>
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}
